import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import authEndpoints from './auth'
import OurClientsEndpoints from './ourClients'
import OfficesEndpoints from './offices'
import RentalsEndpoints from './rentals'
import QuestionsAndAnswersEndpoints from './questionsAndAnswers'
import membershipEndpoints from './membership'
import usersEndpoints from './users'
import OfficeOptionsEndpoints from './office-options'
import feedbackEndpoints from './feedback'
import specificationsEndpoints from './specifications'
import membersdhipPlanEndpoints from './membershipPlan'
import nonMembershipEndpoints from './nonMembership'

// Use environment variables or sensible defaults
const DcoreUrl = process.env.REACT_APP_D_CORE_URL || 'https://braxohub.com/api'
// Prefer explicit menu URL; otherwise default to local dev server
const MenuApiUrl = (process.env.REACT_APP_MENU_API_URL || 'http://localhost:5002/api').replace(/\/$/, '')

const baseQuery = fetchBaseQuery({
  baseUrl: DcoreUrl,
  prepareHeaders: (headers, { endpoint }) => {
    const token = sessionStorage.getItem('refreshToken')
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    // For file upload endpoints, don't set Content-Type (let browser set it)
    if (endpoint !== 'uploadMenuItemImage' &&
      endpoint !== 'addOfficeImage' &&
      endpoint !== 'uploadClientImage' &&
      endpoint !== 'useUploadRentalImage') {
      headers.set('Content-Type', 'application/json');
    }
    headers.set('accept', 'application/json')
    headers.set('lang', 'ENGLISH')
    return headers
  },
})

// Separate base query for menu API (local backend)
const menuBaseQuery = fetchBaseQuery({
  baseUrl: MenuApiUrl,
  prepareHeaders: (headers, { endpoint }) => {
    const token = sessionStorage.getItem('refreshToken')
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    // For file upload endpoints, don't set Content-Type
    if (endpoint !== 'uploadMenuItemImage' &&
      endpoint !== 'uploadEventImage' &&
      endpoint !== 'uploadEventGallery') {
      headers.set('Content-Type', 'application/json');
    }
    headers.set('accept', 'application/json')
    return headers
  },
})

const baseQueryIntercep: typeof baseQuery = async (args, api, extraOptions) => {
  let queryResult = await baseQuery(args, api, extraOptions)
  return queryResult
}

const menuBaseQueryIntercep: typeof menuBaseQuery = async (args, api, extraOptions) => {
  let queryResult = await menuBaseQuery(args, api, extraOptions)
  return queryResult
}

const appApi = createApi({
  reducerPath: 'data',
  baseQuery: (args, api, extraOptions) => {
    // Use menu base query for menu endpoints, regular base query for others
    const isMenuEndpoint = [
      'fetchMenuItems',
      'createMenuItem',
      'updateMenuItem',
      'deleteMenuItem',
      'uploadMenuItemImage',
      'deleteMenuItemImage',
      'fetchMenuItemTransactions',
      'createTransaction',
      // categories under menu API
      'fetchCategories',
      'createCategory',
      'updateCategory',
      'deleteCategory',
      // delete-all menu endpoints
      'deleteAllMenuItems',
      'deleteAllTransactions',
      // events
      'fetchEvents',
      'createEvent',
      'updateEvent',
      'deleteEvent',
      'uploadEventImage',
      'uploadEventGallery'
    ].includes(api.endpoint);

    return isMenuEndpoint
      ? menuBaseQueryIntercep(args, api, extraOptions)
      : baseQueryIntercep(args, api, extraOptions);
  },
  tagTypes: [
    'offices',
    'our-clients',
    'rentals',
    'questions-and-answers',
    'members',
    'users',
    'office-options',
    'specifications',
    'membershipPlans',
    'menu',
    'categories',
    'events',
  ],
  endpoints: (builder) => ({
    ...authEndpoints(builder),
    ...OfficesEndpoints(builder),
    ...OurClientsEndpoints(builder),
    ...RentalsEndpoints(builder),
    ...QuestionsAndAnswersEndpoints(builder),
    ...membershipEndpoints(builder),
    ...usersEndpoints(builder),
    ...OfficeOptionsEndpoints(builder),
    ...feedbackEndpoints(builder),
    ...specificationsEndpoints(builder),
    ...membersdhipPlanEndpoints(builder),
    ...nonMembershipEndpoints(builder),

    // Categories - local backend
    fetchCategories: builder.query<any, any>({
      query: () => '/menu/categories',
      transformResponse: (response: any) => response?.data ?? [],
      providesTags: ['categories']
    }),

    createCategory: builder.mutation<any, any>({
      query: (body) => ({ url: '/menu/categories', method: 'post', body }),
      transformResponse: (response: any) => response?.data ?? response,
      invalidatesTags: ['categories']
    }),

    updateCategory: builder.mutation<any, any>({
      query: ({ id, body }) => ({ url: `/menu/categories/${id}`, method: 'put', body }),
      transformResponse: (response: any) => response?.data ?? response,
      invalidatesTags: ['categories', 'menu']
    }),

    deleteCategory: builder.mutation<any, any>({
      query: (id) => ({ url: `/menu/categories/${id}`, method: 'delete' }),
      invalidatesTags: ['categories', 'menu']
    }),

    // Menu endpoints - Will use local backend
    fetchMenuItems: builder.query<any, any>({
      query: () => '/menu/items',
      // Backend may return an array (simple server) or an object { success, data }
      transformResponse: (response: any) => Array.isArray(response) ? response : response?.data ?? [],
      providesTags: ['menu']
    }),

    createMenuItem: builder.mutation<any, any>({
      query: (body) => ({
        url: '/menu/items',
        method: 'post',
        body,
      }),
      // Unwrap { success, data } to the created item
      transformResponse: (response: any) => response?.data ?? response,
      invalidatesTags: ['menu']
    }),

    deleteMenuItem: builder.mutation<any, any>({
      query: (id) => ({
        url: `/menu/items/${id}`,
        method: 'delete',
      }),
      invalidatesTags: ['menu'],
    }),

    deleteAllMenuItems: builder.mutation<any, void>({
      query: () => ({ url: `/items`, method: 'delete' }),
      invalidatesTags: ['menu']
    }),

    updateMenuItem: builder.mutation<any, any>({
      query: ({ id, body }) => ({
        url: `/menu/items/${id}`,
        method: 'put',
        body,
      }),
      transformResponse: (response: any) => response?.data ?? response,
      invalidatesTags: ['menu'],
    }),

    uploadMenuItemImage: builder.mutation<any, any>({
      query: (body) => ({
        url: '/menu/items/images',
        method: 'post',
        body,
      }),
      invalidatesTags: ['menu'],
    }),

    deleteMenuItemImage: builder.mutation<any, any>({
      query: (id) => ({
        url: `/menu/items/images/${id}`,
        method: 'delete',
      }),
      invalidatesTags: ['menu'],
    }),

    fetchMenuItemTransactions: builder.query<any, any>({
      query: (menuItemId) => `/menu/items/${menuItemId}/transactions`,
      transformResponse: (response: any) => response?.data ?? [],
      providesTags: ['menu']
    }),

    createTransaction: builder.mutation<any, any>({
      query: ({ menuItemId, body }) => ({
        url: `/menu/items/${menuItemId}/transactions`,
        method: 'post',
        body,
      }),
      transformResponse: (response: any) => response?.data ?? response,
      invalidatesTags: ['menu'],
    }),

    deleteAllTransactions: builder.mutation<any, any>({
      query: (menuItemId) => ({
        url: `/menu/items/${menuItemId}/transactions`,
        method: 'delete',
      }),
      invalidatesTags: ['menu']
    }),

    // Events endpoints
    fetchEvents: builder.query<{ success: boolean; data: any[]; count: number }, void>({
      query: () => '/menu/events',
      providesTags: ['events']
    }),

    createEvent: builder.mutation<any, any>({
      query: (body) => ({ url: '/menu/events', method: 'post', body }),
      invalidatesTags: ['events']
    }),

    updateEvent: builder.mutation<any, { id: string, body: any }>({
      query: ({ id, body }) => ({ url: `/menu/events/${id}`, method: 'put', body }),
      invalidatesTags: ['events']
    }),

    deleteEvent: builder.mutation<any, string>({
      query: (id) => ({ url: `/menu/events/${id}`, method: 'delete' }),
      invalidatesTags: ['events']
    }),

    uploadEventImage: builder.mutation<any, any>({
      query: (body) => ({
        url: '/menu/events/images',
        method: 'post',
        body,
      }),
      invalidatesTags: ['events'],
    }),

    uploadEventGallery: builder.mutation<any, { id: string, body: any }>({
      query: ({ id, body }) => ({
        url: `/menu/events/${id}/gallery`,
        method: 'post',
        body,
      }),
      invalidatesTags: ['events'],
    }),

    deleteEventImage: builder.mutation<any, string>({
      query: (id) => ({
        url: `/menu/events/${id}/images`,
        method: 'delete',
      }),
      invalidatesTags: ['events'],
    }),

    deleteEventGalleryImage: builder.mutation<any, { id: string, imageUrl: string }>({
      query: ({ id, imageUrl }) => ({
        url: `/menu/events/${id}/gallery`,
        method: 'delete',
        body: { imageUrl },
      }),
      invalidatesTags: ['events'],
    }),
  }),
})

export default appApi

export const {
  useLoginMutation,

  // Offices 
  useFetchOfficesQuery,
  useCreateOfficeMutation,
  useDeleteOfficeMutation,
  useAddOfficeImageMutation,
  useDeleteOfficeImageMutation,
  useDeleteThisImageMutation,
  useUpdateOfficeMutation,
  useDeleteOfficeSpecificationsMutation,
  useAddOfficeSpecificationMutation,

  // Our Clients
  useFetchOurClientsQuery,
  useCreateOurClientMutation,
  useDeleteOurClientMutation,
  useUpdateOurClientMutation,
  useUploadClientImageMutation,
  useDeleteClientImageMutation,

  // Rentals
  useFetchRentalsQuery,
  useCreateRentalMutation,
  useDeleteRentalMutation,
  useUpdateRentalsMutation,
  useAddSpecificationMutation,
  useDeleteSpecificationMutation,
  useUseUploadRentalImageMutation,
  useDeleteRentalImageMutation,

  // Questions and Answers
  useFetchQuestionsAndAnswersQuery,
  useCreateQuestionAndAnswerMutation,
  useDeleteQuestionAndAnswerMutation,
  useUpdateQuestionAndAnswerMutation,

  // Membership
  useFetchMembersQuery,
  useCreateMemberMutation,

  // Users
  useFetchUsersQuery,
  useCreateUserMutation,
  useAddMembershipMutation,
  useLazySearchAUserQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useAssignMembershipToUserMutation,
  useAssignRentalToUserMutation,
  useDeleteAssignedUserMembershipMutation,
  useDeleteAssignedRentalMutation,
  useHasReturnedRentalMutation,
  useGetAssignedRentalDetailsQuery,
  useFetchUserPaymentsQuery,
  useCompleteUserPaymentMutation,
  useCancelUserRentalMutation,
  useEditMembershipImageMutation,

  // Office Options
  useCreateOfficeOptionMutation,
  useFetchOfficeOptionsQuery,
  useDeleteOfficeOptionMutation,
  useUpdateOfficeOptionMutation,

  useFetchFeedbackQuery,
  useFetchEmailSubscriptionsQuery,

  // specifications
  useFetchSpecificationsQuery,
  useCreateSpecificationMutation,
  useDeleteSpecificationSettingsMutation,
  useUpdateSpecificationMutation,

  // membership plans
  useFetchMembershipPlansQuery,
  useCreateMembershipPlanMutation,
  useDeleteMembershipPlanMutation,
  useUpdateMembershipPlanMutation,

  // non membership
  useFetchNonMembersQuery,
  useCreateNonMemberMutation,
  useUpdateNonMemberMutation,
  useDeleteNonMemberMutation,
  useAssignOfficeToUserMutation,

  // Categories
  useFetchCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,

  // Menu - Updated exports
  useFetchMenuItemsQuery,
  useDeleteAllMenuItemsMutation,
  useDeleteAllTransactionsMutation,
  useCreateMenuItemMutation,
  useUpdateMenuItemMutation,
  useDeleteMenuItemMutation,
  useUploadMenuItemImageMutation,
  useDeleteMenuItemImageMutation,
  useFetchMenuItemTransactionsQuery,
  useCreateTransactionMutation,

  // Events
  useFetchEventsQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useUploadEventImageMutation,
  useUploadEventGalleryMutation,
  useDeleteEventImageMutation,
  useDeleteEventGalleryImageMutation,

} = appApi
